import React from 'react';
import { Link } from 'react-router-dom';
import { PlayCircle, Clock, Award, CheckCircle } from 'lucide-react';
import type { Tutorial } from '../../types/tutorial';

interface TutorialCardProps {
  tutorial: Tutorial;
  isCompleted?: boolean;
}

export default function TutorialCard({ tutorial, isCompleted = false }: TutorialCardProps) {
  // Format category slug for display
  const formatCategoryName = (slug: string) => {
    return slug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <Link
      to={`/tutorials/${tutorial.slug}`}
      className="group bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
    >
      <div className="relative aspect-video bg-gray-200">
        {tutorial.image_url ? (
          <img 
            src={tutorial.image_url} 
            alt={tutorial.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-primary-100">
            <PlayCircle className="h-12 w-12 text-primary-500" />
          </div>
        )}
        {tutorial.video_url && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
            <PlayCircle className="h-16 w-16 text-white" />
          </div>
        )}
        {tutorial.category_slug && (
          <div className="absolute top-2 left-2">
            <span className="bg-primary-500 text-white text-xs font-medium px-2 py-1 rounded-full">
              {formatCategoryName(tutorial.category_slug)}
            </span>
          </div>
        )}
        {isCompleted && (
          <div className="absolute top-2 right-2">
            <div className="bg-green-500 text-white rounded-full p-2 shadow-lg">
              <CheckCircle className="h-5 w-5" />
            </div>
          </div>
        )}
      </div>
      
      <div className="p-6">
        <h3 className="font-semibold text-lg mb-2 group-hover:text-primary-600 transition-colors">
          {tutorial.title}
        </h3>
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
          {tutorial.description}
        </p>
        
        <div className="flex items-center justify-between text-sm text-gray-500">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              <span>{tutorial.estimated_time}</span>
            </div>
            <div className="flex items-center gap-1">
              <Award className="h-4 w-4" />
              <span className="capitalize">{tutorial.difficulty_level}</span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}